import { apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";

import { PRIMARY_LOCALES, type SupportedLocale } from "@/i18n/i18n";

/**
 * Public, whitelisted projection of `platform_setting` (admin-controlled
 * via /admin/settings → Languages tab). Drives which locales the
 * LocaleSwitcher offers without re-deploying when admin toggles a
 * language on or off.
 *
 * Mandatory mandatory fallback: when the API is unreachable or hasn't
 * pinned a list yet, we serve ro + en + bg — matches the contract from
 * apps/web's PlatformConfigProvider and the CLAUDE.md i18n baseline.
 */
export interface PlatformConfig {
  activeLanguages: string[];
}

const FALLBACK: PlatformConfig = { activeLanguages: ["ro", "en", "bg"] };

export function usePlatformConfig() {
  const query = useQuery<PlatformConfig>({
    queryKey: ["platform-config"],
    queryFn: async () => {
      const data = await apiFetch.get<{ active_languages?: string[] }>("/api/platform-config", {
        skipAuthRefresh: true,
      });
      if (Array.isArray(data?.active_languages) && data.active_languages.length > 0) {
        return { activeLanguages: data.active_languages };
      }
      return FALLBACK;
    },
    staleTime: 60 * 60_000, // 1h — admin changes propagate within an hour
    gcTime: 24 * 60 * 60_000,
  });

  return query.data ?? FALLBACK;
}

/**
 * `PRIMARY_LOCALES` ∩ admin-pinned `active_languages`. Use this in the
 * LocaleSwitcher dropdown so we never offer a locale Lingui can't load
 * a catalog for.
 */
export function useActiveLocales(): SupportedLocale[] {
  const { activeLanguages } = usePlatformConfig();
  const allowed = new Set(activeLanguages);
  return PRIMARY_LOCALES.filter((l) => allowed.has(l));
}
